import { useState, useCallback, useEffect, useMemo } from 'react';
import { Columns, PointOfInterestsQueryType } from '@/types';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import {
  useGetStructureNameLazyQuery,
  usePointOfInterestsLazyQuery,
} from '@/__generated__/graphql';
import {
  applyFilters as applyFiltersState,
  selectedTemplateFiltersState,
} from '@/components/Analysis/state';
import { useSelectedAssemblyTemplateId } from '@/components/Analysis/hooks/useSelectedAssemblyTemplateId';
import { useAtomValue } from 'jotai';
import usePoiData from './usePoiData';
import { XlsxProcessor } from '@/utils/xlsx';
import { ALL_POIS_TAB_LABEL } from '@/constants';
import { format } from 'date-fns';

type Props = {
  type: string;
  selectedPoiFilters: {
    id: string;
    value: string[];
  }[];
  paintRegionIds: string[];
  isVerified: boolean[];
  isGoverning: boolean[];
  accessibility: string[];
  status: string[];
  workpackIds: string[];
};

export const useXlsxDownloader = ({
  type,
  selectedPoiFilters,
  paintRegionIds,
  isVerified,
  isGoverning,
  accessibility,
  status,
  workpackIds,
}: Props) => {
  const structureId = useGetStructureId();
  const [pointOfInterests, setPointOfInterests] = useState<PointOfInterestsQueryType>([]);
  const [structureName, setStructureName] = useState<string>('');
  const [dataFetched, setDataFetched] = useState(false);
  const applyFilters = useAtomValue(applyFiltersState);
  const selectedTemplateFilters = useAtomValue(selectedTemplateFiltersState);
  const selectedAssemblyTemplateId = useSelectedAssemblyTemplateId();
  const [appliedTemplateFilters, setAppliedTemplateFilters] = useState(selectedTemplateFilters);

  const [columns, setColumns] = useState<Columns[]>([]);
  const [includePlatformName, setIncludePlatformName] = useState<boolean>(false);

  const validColumnsForXLSX = useMemo<Columns[]>(
    () => [
      ...columns.filter(({ key }) => key !== 'view3D'),
      {
        key: 'imageName',
        columnName: 'Image Name',
        visibility: true,
        isSortable: true,
      },
      {
        key: 'imageLink',
        columnName: 'Image Link',
        visibility: true,
        isSortable: true,
      },
    ],
    [columns]
  );

  useEffect(() => {
    if (applyFilters) {
      setAppliedTemplateFilters(selectedTemplateFilters);
    }
  }, [applyFilters, selectedTemplateFilters]);

  const [getStructureName] = useGetStructureNameLazyQuery({
    variables: { structureId },
    fetchPolicy: 'cache-first',
  });

  const [getPointOfInterestsForXlsx] = usePointOfInterestsLazyQuery({
    variables: {
      input: {
        structureId,
        assemblyTemplateId: selectedAssemblyTemplateId,
        selectedTemplateFilters: appliedTemplateFilters,
        type,
        selectedPoiFilters,
        includeManuallyCreated: true,
        workpackIds,
        paintRegionIds,
        isVerified,
        isGoverning,
        accessibility,
        status,
      },
    },
    fetchPolicy: 'no-cache',
  });

  const { cols } = usePoiData({
    pointOfInterests,
    columns: validColumnsForXLSX,
    hiddenColumns: [],
  });

  // Define the input column data type
  interface ColumnData {
    key: string;
    columnName?: string;
    data?: Array<{ key: string; value: string | number | boolean | null | undefined }>;
  }

  // Add this type to match what usePoiData returns
  // interface PoiColumn {
  //   label: string;
  //   data: (string | number | boolean)[];
  // }

  // Transform columns to the format expected by XlsxProcessor
  const transformColumnsForXlsx = useCallback((columns: ColumnData[]) => {
    if (!columns || columns.length === 0) {
      return [];
    }

    // Filter out columns without data
    const columnsWithData = columns.filter((col) => col.data && col.data.length > 0);

    if (columnsWithData.length === 0) {
      return [];
    }

    // Get the maximum number of rows from all columns
    const maxRows = Math.max(...columnsWithData.map((col) => col.data?.length || 0));

    if (maxRows === 0) {
      return [];
    }

    // Create result with proper column structure
    return columnsWithData.map((column) => {
      // Extract data for this column
      const data = Array(maxRows).fill('');

      if (column.data) {
        column.data.forEach((item, index) => {
          if (index < maxRows) {
            data[index] = item.value !== undefined ? item.value : '';
          }
        });
      }

      return {
        label: column.columnName || column.key,
        data,
      };
    });
  }, []);

  const xlsxData = useMemo(() => {
    // Convert cols to the format expected by transformColumnsForXlsx
    const convertedCols: ColumnData[] = cols.map((col, index) => ({
      key: col.label || `column_${index}`,
      columnName: col.label,
      data: col.data.map((value, rowIndex) => ({
        key: `row_${rowIndex}`,
        value,
      })),
    }));

    // Add Platform Name column if it's selected and structureName is available
    if (includePlatformName && structureName) {
      const rowCount = cols.length > 0 ? cols[0].data.length : 0;
      const platformNameColumn: ColumnData = {
        key: 'platformName',
        columnName: 'Platform Name',
        data: Array(rowCount)
          .fill(null)
          .map((_, rowIndex) => ({
            key: `row_${rowIndex}`,
            value: structureName,
          })),
      };
      convertedCols.push(platformNameColumn);
    }

    const transformedColumns = transformColumnsForXlsx(convertedCols);

    return new XlsxProcessor({
      columns: transformedColumns,
      sheetName: 'Points of Interest',
    });
  }, [cols, transformColumnsForXlsx, structureName, includePlatformName]);

  // Converting due date from timestamp to formatted stringified date
  const selectedPoiTypeName = useMemo(() => type ?? ALL_POIS_TAB_LABEL, [type]);

  const xlsxFile = useCallback(async () => {
    if (xlsxData) {
      const date = format(new Date(), 'dd-MMM-yyyy');
      // File name format similar to CSV
      const fileName = `${structureName}-${selectedPoiTypeName}-${date}.xlsx`;
      await xlsxData.download(fileName);
    }
  }, [xlsxData, selectedPoiTypeName, structureName]);

  const downloadXLSX = useCallback(
    async (selectedColumns: Columns[], includePlatformNameFlag: boolean = false) => {
      setColumns(selectedColumns);
      setIncludePlatformName(includePlatformNameFlag);
      const [pointOfInterestsResult, structureNameQueryResult] = await Promise.all([
        getPointOfInterestsForXlsx(),
        getStructureName(),
      ]);

      if (pointOfInterestsResult?.data?.pointOfInterests) {
        setPointOfInterests(pointOfInterestsResult.data.pointOfInterests);
      }

      setStructureName(structureNameQueryResult?.data?.structure?.name ?? 'platform');

      setDataFetched(true);
    },
    [getPointOfInterestsForXlsx, getStructureName]
  );

  useEffect(() => {
    if (dataFetched) {
      xlsxFile();
      setDataFetched(false);
    }
  }, [dataFetched, xlsxFile]);

  return { downloadXLSX };
};
